import React from 'react';

import { Box, Flex, Image, Show, Text } from '@chakra-ui/react';
import ArrowIcon from 'images/icons/arrow-right.svg';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { Button } from '@/components/atoms';

type StickyAdProps = {
  headingAd: string;
  textAd: string;
  buttonCaptionAd: string;
  buttonUrlAd: string;
  hasDarkBackground: boolean;
  imageAd?: {
    __typename?: 'UploadFileEntityResponse';
    data?: {
      __typename?: 'UploadFileEntity';
      attributes?: {
        __typename?: 'UploadFile';
        url: string;
        alternativeText?: string | null;
      } | null;
    } | null;
  } | null;
};

export const StickyAd = ({
  headingAd,
  textAd,
  buttonCaptionAd,
  buttonUrlAd,
  hasDarkBackground,
  imageAd,
}: StickyAdProps) => {
  return (
    <Box
      bg={hasDarkBackground ? 'surface-dark-highlight' : 'surface-light-highlight'}
      maxW={{ base: '628px', lg: '628px', xl: '261px' }}
      w={{ base: '100%', xl: 'auto' }}
      overflow="hidden"
    >
      <Show above="xl">
        {imageAd?.data?.attributes?.url && (
          <Image
            src={imageAd.data.attributes.url}
            alt={imageAd.data.attributes.alternativeText || 'Ad image'}
            borderRadius="md"
            w="100%"
            h="auto"
            py={{ base: 10, xl: 5 }}
          />
        )}
      </Show>
      <Flex
        flexDirection={{ base: 'column', xs: 'row', xl: 'column' }}
        gap={5}
        alignItems={{ base: 'stretch', lg: 'flex-start' }}
      >
        <Flex flexDirection="column" gap={5} p={{ base: 5, sm: 10, xl: 5 }} flex="1">
          <Text
            variant={{
              base: 'text-4xl/lineHeight-10/font-normal',
              md: 'text-5xl/lineHeight-11/font-normal',
              xl: 'text-2xl/lineHeight-8/font-normal',
            }}
            fontWeight="regular"
            color={hasDarkBackground ? 'white' : 'text-dark.500'}
          >
            {headingAd}
          </Text>

          <Box
            sx={{
              color: hasDarkBackground ? 'text.300' : 'text-dark.300',
              fontSize: { base: 'md', xl: 'sm' },
              lineHeight: { base: 'md', xl: 'sm' },
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            <ReactMarkdown
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                p: ({ children }) => (
                  <Text as="p" margin={0} marginBottom="0.5rem">
                    {children}
                  </Text>
                ),
                strong: ({ children }) => (
                  <Text as="strong" fontWeight="bold">
                    {children}
                  </Text>
                ),
                em: ({ children }) => (
                  <Text as="em" fontStyle="italic">
                    {children}
                  </Text>
                ),
                ul: ({ children }) => (
                  <Box as="ul" margin={0} paddingLeft="1.5rem" marginBottom="0.5rem">
                    {children}
                  </Box>
                ),
                ol: ({ children }) => (
                  <Box as="ol" margin={0} paddingLeft="1.5rem" marginBottom="0.5rem">
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Box as="li" marginBottom="0.25rem">
                    {children}
                  </Box>
                ),
              }}
            >
              {textAd}
            </ReactMarkdown>
          </Box>

          <Button
            variant="light"
            label={buttonCaptionAd}
            url={buttonUrlAd}
            width={{ base: '100%', xl: 'auto' }}
            size="md"
            maxW="220px"
            px={{ base: 6, xl: 3 }}
            py={{ base: 3, xl: 1.5 }}
            rightIcon={<ArrowIcon width="12px" />}
          />
        </Flex>
        <Show below="xl">
          {imageAd?.data?.attributes?.url && (
            <Image
              src={imageAd.data.attributes.url}
              alt={imageAd.data.attributes.alternativeText || 'Ad image'}
              h="100%"
              maxH="400px"
              objectFit="contain"
              position="relative"
              left={{ base: '100px', sm: '0px' }}
            />
          )}
        </Show>
      </Flex>
    </Box>
  );
};
